import React, { useState } from 'react';
import plusMark from '../assets/sonmi-plus-mark.png';

/**
 * Son:mi+ paywall — shown when a free account is over its monthly advisor-message limit
 * (the backend returns { paywall: true }). Editorial Son:mi brand: serif wordmark, warm
 * cream, bronze accents, maroon CTA. Built from Sofia's approved design (founding £19.99).
 *
 * Props:
 *   isOpen       — show/hide
 *   onClose      — dismiss (paywall is dismissable; chat stays blocked until they subscribe)
 *   onSubscribe  — called with 'founding' | 'monthly'; the caller kicks off Stripe Checkout
 *   busy         — true while a checkout session is being created (disables the CTA)
 *
 * Copy, benefit lines and check marks follow the agreed mockup
 * (mockups/founding-membership-paywall.html). White ground, real Son:mi+ mark.
 * The 100-a-month fair-use cap is stated once in the fine print rather than sold as a
 * benefit: it is a limit, not a feature, but hiding it entirely would not be fair.
 * No em dashes and all text is >= 13px, per the house rules.
 */

const BRONZE = '#A97B34';

const Icon = ({ d }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3"
       strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
       style={{ width: 26, height: 26 }}>
    {d}
  </svg>
);

const BENEFITS = [
  {
    label: 'Personal advice and product checks',
    icon: <Icon d={<><rect x="3" y="4.6" width="18" height="13" rx="3.2" />
      <path d="M8.4 17.6 7 21l4.2-3.4" />
      <path d="M12 14.2s-2.5-1.6-2.5-3.2a1.45 1.45 0 0 1 2.5-1 1.45 1.45 0 0 1 2.5 1c0 1.6-2.5 3.2-2.5 3.2Z" /></>} />,
  },
  {
    label: 'Cross-brand shade matching',
    icon: <Icon d={<path d="M12 3.2c3.2 3.4 5.6 6.3 5.6 9.1a5.6 5.6 0 0 1-11.2 0c0-2.8 2.4-5.7 5.6-9.1Z" />} />,
  },
  {
    label: 'Skin and routine tracking',
    icon: <Icon d={<><path d="M3.6 20.4h16.8" /><path d="M3.6 20.4V4.2" />
      <path d="M6 16.4 10 11l3.4 2.6L19.4 6" />
      <circle cx="10" cy="11" r="1.1" /><circle cx="13.4" cy="13.6" r="1.1" />
      <circle cx="19.4" cy="6" r="1.1" /></>} />,
  },
];

export default function PlusPaywall({ isOpen, onClose, onSubscribe, busy = false }) {
  const [plan, setPlan] = useState('founding');
  if (!isOpen) return null;

  const ink = '#2A1212';
  const Card = ({ id, children }) => {
    const sel = plan === id;
    return (
      <button
        type="button"
        onClick={() => setPlan(id)}
        style={{
          position: 'relative', width: '100%', textAlign: 'left', cursor: 'pointer',
          background: sel ? '#FBF6EE' : '#fff',
          border: `1.5px solid ${sel ? BRONZE : '#ECE3D6'}`, borderRadius: 16,
          padding: '18px 16px', display: 'flex', alignItems: 'center', gap: 12,
        }}
      >
        <span style={{ flex: 1 }}>{children}</span>
        <span style={{
          width: 22, height: 22, borderRadius: '50%', flex: '0 0 22px',
          border: `2px solid ${sel ? BRONZE : '#D8CDBD'}`, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
        }}>
          {sel && <span style={{ width: 11, height: 11, borderRadius: '50%', background: BRONZE }} />}
        </span>
      </button>
    );
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1100, background: '#FFFFFF',
      overflowY: 'auto', fontFamily: "'DM Sans', -apple-system, sans-serif",
      display: 'flex', flexDirection: 'column', alignItems: 'center',
    }}>
      <div style={{ width: '100%', maxWidth: 440, padding: '18px 24px 34px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            width: 34, height: 34, borderRadius: '50%', border: '1px solid #E7DED2',
            background: '#fff', cursor: 'pointer', color: '#6b615a', fontSize: 16, lineHeight: 1,
          }}>✕</button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6, marginBottom: 4 }}>
          <img src={plusMark} alt="" aria-hidden="true"
               style={{ width: 128, height: 128, objectFit: 'contain' }} />
        </div>

        <h1 style={{
          fontFamily: 'var(--font-sonmi-display)', fontWeight: 400,
          fontSize: 52, textAlign: 'center', color: ink, letterSpacing: '-0.015em', margin: 0,
        }}>Son:mi+</h1>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, margin: '28px 4px 26px' }}>
          {BENEFITS.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ flex: '0 0 26px', color: ink, display: 'flex' }}>{b.icon}</span>
              <span style={{ fontSize: 15, color: '#3A3230', lineHeight: 1.35 }}>{b.label}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
          <Card id="founding">
            <span style={{
              display: 'inline-block', background: '#F4EAD6', color: '#8A6A24',
              fontSize: 13, fontWeight: 700, letterSpacing: '0.03em',
              padding: '3px 10px', borderRadius: 100, marginBottom: 8,
            }}>FOUNDING MEMBERSHIP · 52% OFF</span>
            <div style={{ fontSize: 30, fontWeight: 800, color: ink, letterSpacing: '-0.02em' }}>
              £19.99 <span style={{ fontSize: 16, fontWeight: 500, color: '#4A413B' }}>for your first year</span>
            </div>
            <div style={{ fontSize: 13.5, color: '#9b9186', marginTop: 4 }}>
              Renews at £39.99/year · £3.33/month
            </div>
          </Card>

          <Card id="monthly">
            <div style={{ fontSize: 17, color: ink, fontWeight: 600 }}>Monthly</div>
            <div style={{ fontSize: 30, fontWeight: 800, color: ink, letterSpacing: '-0.02em', marginTop: 2 }}>
              £6.99 <span style={{ fontSize: 15, fontWeight: 500, color: '#9b9186' }}>/month</span>
            </div>
          </Card>
        </div>

        <button
          type="button"
          disabled={busy}
          onClick={() => onSubscribe?.(plan)}
          style={{
            width: '100%', border: 'none', borderRadius: 100, padding: '17px 16px',
            background: ink, color: '#fff', fontSize: 16, fontWeight: 700,
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1,
          }}
        >
          {busy ? 'One moment…' : (plan === 'founding' ? 'Start my founding membership' : 'Subscribe')}
        </button>

        <p style={{ textAlign: 'center', fontSize: 13, color: '#9b9186', margin: '12px 8px 0', lineHeight: 1.4 }}>
          Cancel anytime. Fair use: 100 advisor messages a month.
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 18 }}>
          {['Restore purchase', 'Terms', 'Privacy'].map((t) => (
            <button key={t} type="button" style={{
              background: 'none', border: 'none', fontSize: 13, color: '#6f655c',
              textDecoration: 'underline', cursor: 'pointer',
            }}>{t}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
